import c3 from "c3";
import d3 from "d3";

import { UnitSystem } from "@/Common/Models/Patient/PatientEnums";
import { GrowthChartsCurves, GrowthChartsLine, PercentileOption } from "@/Patient/Models/GrowthCharts/LoadGrowthCurvesCacheResponse";
import { AgeUnit } from "./BaseChartModel";

interface ChartDataPointBase {
    id?: string;
    medicalItemId?: string;
}

interface ChartDataPoint extends ChartDataPointBase {
    id?: string;
    medicalItemId?: string;
    x: string;
    y: string;
    tooltipValue?: string;
}

interface AxisConfiguration {
    label: string;
    interval?: number;
    min?: number;
    max?: number;
}

interface AreaLabelConfiguration {
    label: string;
    class: string;
    x: number,
    y: number,
    color: string
}

class DefaultChartData {
    private xyNamePair: { [key: string]: string };
    private xyValuePair: { [key: string]: string[] };
    private unitSystem: UnitSystem;

    public p01: ChartDataPoint[];
    public p3: ChartDataPoint[];
    public p15: ChartDataPoint[];
    public p50: ChartDataPoint[];
    public p85: ChartDataPoint[];
    public p97: ChartDataPoint[];
    public p99: ChartDataPoint[];

    public constructor(
        curves: GrowthChartsCurves,
        ageUnit: AgeUnit,
        unitSystem: UnitSystem,
        private xUpperBound: number,
        private yUpperBound: number
    ) {
        this.unitSystem = unitSystem;
        const XValues = (unitSystem === UnitSystem.Imperial && !curves?.IsXUnitAgnostic)
            ? curves?.XImperial
            : curves?.X;

        this.p01 = this.mapPercentile(XValues, curves?.Lines, "P0.1", ageUnit, curves?.IsYUnitAgnostic);
        this.p3 = this.mapPercentile(XValues, curves?.Lines, "P3", ageUnit, curves?.IsYUnitAgnostic);
        this.p15 = this.mapPercentile(XValues, curves?.Lines, "P15", ageUnit, curves?.IsYUnitAgnostic);
        this.p50 = this.mapPercentile(XValues, curves?.Lines, "P50", ageUnit, curves?.IsYUnitAgnostic);
        this.p85 = this.mapPercentile(XValues, curves?.Lines, "P85", ageUnit, curves?.IsYUnitAgnostic);
        this.p97 = this.mapPercentile(XValues, curves?.Lines, "P97", ageUnit, curves?.IsYUnitAgnostic);
        this.p99 = this.mapPercentile(XValues, curves?.Lines, "P99.9", ageUnit, curves?.IsYUnitAgnostic);
    }

    public equals(other: DefaultChartData): boolean {
        return this.p01.length === other.p01.length
            && this.p3.length === other.p3.length
            && this.p15.length === other.p15.length
            && this.p50.length === other.p50.length
            && this.p85.length === other.p85.length
            && this.p97.length === other.p97.length
            && this.p99.length === other.p99.length
            && !this.p01.some((p, idx) => p.id !== other.p01[idx].id || p.x !== other.p01[idx].x || p.y !== other.p01[idx].y)
            && !this.p3.some((p, idx) => p.id !== other.p3[idx].id || p.x !== other.p3[idx].x || p.y !== other.p3[idx].y)
            && !this.p15.some((p, idx) => p.id !== other.p15[idx].id || p.x !== other.p15[idx].x || p.y !== other.p15[idx].y)
            && !this.p50.some((p, idx) => p.id !== other.p50[idx].id || p.x !== other.p50[idx].x || p.y !== other.p50[idx].y)
            && !this.p85.some((p, idx) => p.id !== other.p85[idx].id || p.x !== other.p85[idx].x || p.y !== other.p85[idx].y)
            && !this.p97.some((p, idx) => p.id !== other.p97[idx].id || p.x !== other.p97[idx].x || p.y !== other.p97[idx].y)
            && !this.p99.some((p, idx) => p.id !== other.p99[idx].id || p.x !== other.p99[idx].x || p.y !== other.p99[idx].y);
    }

    public toC3XS(): { [key: string]: string } {
        this.generateXyNamePairIfNeeded();
        return { ...this.xyNamePair };
    }

    public toC3Columns(): c3.PrimitiveArray[] {
        this.generateXyNamePairIfNeeded();
        this.generateXyValuePairIfNeeded();

        const columns = [];
        for (let x in this.xyNamePair) {
            let y = this.xyNamePair[x];

            columns.push(([x] as c3.PrimitiveArray).concat(this.xyValuePair[x]));
            columns.push(([y] as c3.PrimitiveArray).concat(this.xyValuePair[y]));
        }

        return columns;
    }

    public toC3Types(): { [key: string]: string } {
        const types: { [key: string]: string } = {};

        for (let key in this.xyNamePair) {
            if (!types[key])
                types[key] = "spline"; // spline mean curvy line in c3 documentation
        }
        return { ...types };
    }

    public toC3Classes(): { [key: string]: string } {
        const classes: { [key: string]: string } = {};

        for (let key in this.xyNamePair) {
            if (!classes[key])
                classes[key] = "hide-circle"; // spline mean curvy line in c3 documentation

            if (key.startsWith("p99"))
                classes[key] = (classes[key] ?? "") + " c3-curve-dashed";
            else if (key.startsWith("p01"))
                classes[key] = (classes[key] ?? "") + " c3-curve-dashed";
        }
        return { ...classes };
    }

    public toC3Colors(): { [key: string]: string | d3.Rgb } {
        const colors: { [key: string]: string } = {};

        for (let key in this.xyNamePair) {
            if (colors[key])
                continue;

            if (key.startsWith("p3"))
                colors[key] = "#A41CFF";
            else if (key.startsWith("p15"))
                colors[key] = "#EE665B";
            else if (key.startsWith("p50"))
                colors[key] = "#7BB263";
            else if (key.startsWith("p85"))
                colors[key] = "#FFA51D";
            else if (key.startsWith("p97"))
                colors[key] = "#1B5CCE";
            else if (key.startsWith("p99"))
                colors[key] = "#000000";
            else if (key.startsWith("p01"))
                colors[key] = "#000000";
        }
        return { ...colors };
    }

    public toC3Names(): { [key: string]: string; } {
        const names: { [key: string]: string } = {};
        if (!this.xyNamePair)
            this.generateXyNamePairIfNeeded();

        for (let key in this.xyNamePair) {
            if (names[key])
                continue;

            if (key.startsWith("p01"))
                names[key] = "&nbsp";
            else if (key.startsWith("p3"))
                names[key] = "3";
            else if (key.startsWith("p15"))
                names[key] = "15";
            else if (key.startsWith("p50"))
                names[key] = "50";
            else if (key.startsWith("p85"))
                names[key] = "85";
            else if (key.startsWith("p97"))
                names[key] = "97";
            else if (key.startsWith("p99"))
                names[key] = "&nbsp";
        }
        return { ...names };
    }

    private generateXyNamePairIfNeeded(): void {
        if (this.xyNamePair)
            return;

        this.xyNamePair = {};
        const fields = Object.keys(this);
        fields.forEach(name => {
            if (name.startsWith("p") && !this.xyNamePair[`${name}_y`] && this[name].length) {
                this.xyNamePair[`${name}_y`] = `${name}_x`;
            }
        });
    }

    private generateXyValuePairIfNeeded(): void {
        if (this.xyValuePair)
            return;

        this.xyValuePair = {};

        for (let key in this.xyNamePair) {
            let value = this.xyNamePair[key];

            if (key.startsWith("p01") && this.p3.length) {
                this.xyValuePair[key] = this.p01.map(p => p.y);
                this.xyValuePair[value] = this.p01.map(p => p.x);
            }
            else if (key.startsWith("p3") && this.p3.length) {
                this.xyValuePair[key] = this.p3.map(p => p.y);
                this.xyValuePair[value] = this.p3.map(p => p.x);
            }
            else if (key.startsWith("p15") && this.p15.length) {
                this.xyValuePair[key] = this.p15.map(p => p.y);
                this.xyValuePair[value] = this.p15.map(p => p.x);
            }
            else if (key.startsWith("p50") && this.p50.length) {
                this.xyValuePair[key] = this.p50.map(p => p.y);
                this.xyValuePair[value] = this.p50.map(p => p.x);
            }
            else if (key.startsWith("p85") && this.p85.length) {
                this.xyValuePair[key] = this.p85.map(p => p.y);
                this.xyValuePair[value] = this.p85.map(p => p.x);
            }
            else if (key.startsWith("p97") && this.p97.length) {
                this.xyValuePair[key] = this.p97.map(p => p.y);
                this.xyValuePair[value] = this.p97.map(p => p.x);
            }
            else if (key.startsWith("p99") && this.p97.length) {
                this.xyValuePair[key] = this.p99.map(p => p.y);
                this.xyValuePair[value] = this.p99.map(p => p.x);
            }
        }
    }

    private mapPercentile(x: number[], lines: GrowthChartsLine[], option: PercentileOption, ageUnit: AgeUnit, isUnitAgnostic: boolean): ChartDataPoint[] {
        const y = this.unitSystem === UnitSystem.Imperial && !isUnitAgnostic
            ? lines?.firstOrDefault(p => p.Label === option)?.LineValuesImperial
            : lines?.firstOrDefault(p => p.Label === option)?.LineValues;

        if (!x?.length || !y?.length)
            return [];

        const dataPoints: ChartDataPoint[] = [];
        for (let i = 0; i < y.length; i++) {
            if (x[i] == null || y[i] == null)
                continue;

            const xValue = this.getXValue(ageUnit, x[i]);
            if (xValue > this.xUpperBound || y[i] > this.yUpperBound)
                continue;

            dataPoints.push({
                x: xValue.toString(),
                y: y[i] + ""
            });
        }

        return dataPoints;
    }

    private getXValue(ageUnit: AgeUnit, x: number): number {
        if (!ageUnit)
            return x;

        return (ageUnit === "year" ? x / 12 : x);
    }
}

export { AreaLabelConfiguration, AxisConfiguration, ChartDataPoint, ChartDataPointBase, DefaultChartData };
